import {
  publishGovernorRuntimeEvidence,
} from '../runtime-evidence/GovernorRuntimeEvidenceBus';

import {
  emitTreeGovernorEvent,
} from '../../lib/graph/graphMutationAudit';

import {
  clearCanonicalTreeHistoricalAuthorityPresentationLifecycleForTest,
  readCanonicalTreeHistoricalAuthorityPresentationLifecycle,
  recordCanonicalTreeHistoricalAuthorityPresentationLifecycleFailure,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycle';

import type {
  CanonicalTreeHistoricalAuthorityPresentationContainmentReason,
} from './CanonicalTreeHistoricalAuthorityPresentationFailureContainment';

export type CanonicalTreeHistoricalAuthorityPresentationObservabilityEventKind =
  | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FALLBACK_ACTIVATED'
  | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE';

export type CanonicalTreeHistoricalAuthorityPresentationObservabilityReason =
  | CanonicalTreeHistoricalAuthorityPresentationContainmentReason
  | 'CARD_RENDER_FAILURE';

export interface CanonicalTreeHistoricalAuthorityPresentationObservabilityRecord {
  readonly milestone:
    'CC-TREE-ARCH-079';

  readonly eventKind:
    CanonicalTreeHistoricalAuthorityPresentationObservabilityEventKind;

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationObservabilityReason;

  readonly sourceMilestone:
    'CC-TREE-ARCH-076';

  readonly containmentStatus:
    'FALLBACK';

  readonly correlationId:
    string;

  readonly occurrenceCount:
    number;

  readonly occurredAt:
    string;

  readonly safeFailureMessage:
    string | null;

  readonly classification:
    'TREE_GOVERNOR_INTERNAL_ONLY';

  readonly containsProjectionPayload:
    false;

  readonly containsFamilyMemberData:
    false;

  readonly containsRelationshipData:
    false;

  readonly containsAuthenticationData:
    false;

  readonly containsMembershipData:
    false;

  readonly containsStackTrace:
    false;

  readonly affectsRuntimeDecision:
    false;

  readonly affectsRuntimeGuard:
    false;

  readonly affectsOperationalCertification:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly memberFacing:
    false;
}

export interface CanonicalTreeHistoricalAuthorityPresentationObservabilitySnapshot {
  readonly generatedAt:
    string;

  readonly totalRecords:
    number;

  readonly records:
    readonly CanonicalTreeHistoricalAuthorityPresentationObservabilityRecord[];
}

export interface CanonicalTreeHistoricalAuthorityPresentationObservabilityImplementation {
  readonly milestone:
    'CC-TREE-ARCH-079';

  readonly authority:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_IMPLEMENTATION';

  readonly decision:
    'TREE_INTERNAL_DURABLE_CONTAINMENT_OBSERVABILITY_IMPLEMENTED';

  readonly auditMilestone:
    'CC-TREE-ARCH-078';

  readonly boundedHistoryCapacity:
    64;

  readonly projectionFallbackPublicationImplemented:
    true;

  readonly renderFailurePublicationImplemented:
    true;

  readonly occurrenceTimestampImplemented:
    true;

  readonly deterministicCorrelationIdImplemented:
    true;

  readonly occurrenceCountImplemented:
    true;

  readonly safeFailureMessageImplemented:
    true;

  readonly existingTreeGovernorEvidenceReused:
    true;

  readonly existingRuntimeEvidenceReused:
    true;

  readonly canonicalGovernorRuntimeDispatchInvoked:
    false;

  readonly projectionPayloadPublished:
    false;

  readonly familyMemberDataPublished:
    false;

  readonly stackTracePublished:
    false;

  readonly runtimeDecisionDependencyAdded:
    false;

  readonly runtimeGuardDependencyAdded:
    false;

  readonly operationalCertificationDependencyAdded:
    false;

  readonly memberFacingStateAdded:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly implementationCompleted:
    true;

  readonly nextGovernedDependency:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_CERTIFICATION';
}

const FAILURE_MESSAGE_LIMIT =
  240;

const sanitizeFailureMessage = (
  value:
    unknown,
): string | null => {
  if (value == null) {
    return null;
  }

  const normalized =
    String(value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(
        0,
        FAILURE_MESSAGE_LIMIT,
      );

  return normalized.length > 0
    ? normalized
    : null;
};

const stableHash = (
  value:
    string,
): string => {
  let hash =
    2166136261;

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    hash ^=
      value.charCodeAt(index);

    hash =
      Math.imul(
        hash,
        16777619,
      );
  }

  return (
    hash >>> 0
  )
    .toString(16)
    .padStart(8, '0')
    .toUpperCase();
};

export const resolveCanonicalTreeHistoricalAuthorityPresentationCorrelationId =
  (
    eventKind:
      CanonicalTreeHistoricalAuthorityPresentationObservabilityEventKind,

    reason:
      CanonicalTreeHistoricalAuthorityPresentationObservabilityReason,
  ): string =>
    `TREE-HIST-AUTH-${stableHash(
      [
        eventKind,
        reason,
        'CC-TREE-ARCH-076',
      ].join('|'),
    )}`;

const createCompatibilityRecord = (
  input:
    Readonly<{
      eventKind:
        CanonicalTreeHistoricalAuthorityPresentationObservabilityEventKind;

      reason:
        CanonicalTreeHistoricalAuthorityPresentationObservabilityReason;

      correlationId:
        string;

      occurrenceCount:
        number;

      occurredAt:
        string;

      safeFailureMessage:
        string | null;
    }>,
): CanonicalTreeHistoricalAuthorityPresentationObservabilityRecord =>
  Object.freeze({
    milestone:
      'CC-TREE-ARCH-079',

    eventKind:
      input.eventKind,

    reason:
      input.reason,

    sourceMilestone:
      'CC-TREE-ARCH-076',

    containmentStatus:
      'FALLBACK',

    correlationId:
      input.correlationId,

    occurrenceCount:
      input.occurrenceCount,

    occurredAt:
      input.occurredAt,

    safeFailureMessage:
      input.safeFailureMessage,

    classification:
      'TREE_GOVERNOR_INTERNAL_ONLY',

    containsProjectionPayload:
      false,

    containsFamilyMemberData:
      false,

    containsRelationshipData:
      false,

    containsAuthenticationData:
      false,

    containsMembershipData:
      false,

    containsStackTrace:
      false,

    affectsRuntimeDecision:
      false,

    affectsRuntimeGuard:
      false,

    affectsOperationalCertification:
      false,

    certifiesTreeHealth:
      false,

    certifiesReleaseReadiness:
      false,

    certifiesDeploymentReadiness:
      false,

    memberFacing:
      false,
  });

const publishExistingGovernorEvidence = (
  record:
    CanonicalTreeHistoricalAuthorityPresentationObservabilityRecord,
): void => {
  try {
    emitTreeGovernorEvent(
      record.eventKind,
      {
        milestone:
          record.milestone,

        reason:
          record.reason,

        sourceMilestone:
          record.sourceMilestone,

        containmentStatus:
          record.containmentStatus,

        correlationId:
          record.correlationId,

        occurrenceCount:
          record.occurrenceCount,

        occurredAt:
          record.occurredAt,

        safeFailureMessage:
          record.safeFailureMessage,

        classification:
          record.classification,

        containsPrivateFamilyData:
          false,

        affectsRuntimeAuthority:
          false,

        certifiesTreeHealth:
          false,

        certifiesReleaseReadiness:
          false,

        certifiesDeploymentReadiness:
          false,
      },
    );
  } catch {
    // Existing Tree evidence publication is best effort.
  }

  try {
    publishGovernorRuntimeEvidence({
      domain:
        'tree',

      source:
        'CC-TREE-ARCH-079',

      severity:
        'warn',

      summary:
        record.eventKind ===
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE'
          ? 'Tree historical-authority presentation rendering failed safely.'
          : 'Tree historical-authority presentation fallback activated.',

      evidence:
        [
          `reason=${record.reason}`,
          `correlationId=${record.correlationId}`,
          `occurrenceCount=${record.occurrenceCount}`,
          `occurredAt=${record.occurredAt}`,
          'classification=TREE_GOVERNOR_INTERNAL_ONLY',
        ],

      owner:
        'tree-governor',

      repairReady:
        false,
    });
  } catch {
    // Runtime evidence publication is best effort.
  }
};

export const publishCanonicalTreeHistoricalAuthorityPresentationObservability =
  (
    input:
      Readonly<{
        eventKind:
          CanonicalTreeHistoricalAuthorityPresentationObservabilityEventKind;

        reason:
          CanonicalTreeHistoricalAuthorityPresentationObservabilityReason;

        failureMessage?:
          unknown;

        occurredAt?:
          string;
      }>,
  ): CanonicalTreeHistoricalAuthorityPresentationObservabilityRecord | null => {
    try {
      const occurredAt =
        input.occurredAt &&
        !Number.isNaN(
          Date.parse(
            input.occurredAt,
          ),
        )
          ? input.occurredAt
          : new Date().toISOString();

      const safeFailureMessage =
        sanitizeFailureMessage(
          input.failureMessage,
        );

      const correlationId =
        resolveCanonicalTreeHistoricalAuthorityPresentationCorrelationId(
          input.eventKind,
          input.reason,
        );

      const lifecycleRecord =
        recordCanonicalTreeHistoricalAuthorityPresentationLifecycleFailure({
          eventKind:
            input.eventKind,

          reason:
            input.reason,

          correlationId,

          occurredAt,

          safeFailureMessage,
        });

      if (!lifecycleRecord) {
        return null;
      }

      const record =
        createCompatibilityRecord({
          eventKind:
            input.eventKind,

          reason:
            input.reason,

          correlationId:
            lifecycleRecord.correlationId,

          occurrenceCount:
            lifecycleRecord.occurrenceCount,

          occurredAt:
            lifecycleRecord.occurredAt,

          safeFailureMessage:
            lifecycleRecord.safeFailureMessage,
        });

      publishExistingGovernorEvidence(
        record,
      );

      return record;
    } catch {
      return null;
    }
  };

export const readCanonicalTreeHistoricalAuthorityPresentationObservability =
  ():
  CanonicalTreeHistoricalAuthorityPresentationObservabilitySnapshot => {
    const lifecycleSnapshot =
      readCanonicalTreeHistoricalAuthorityPresentationLifecycle();

    const records =
      lifecycleSnapshot.records.map(
        lifecycleRecord =>
          createCompatibilityRecord({
            eventKind:
              lifecycleRecord.eventKind,

            reason:
              lifecycleRecord.reason,

            correlationId:
              lifecycleRecord.correlationId,

            occurrenceCount:
              lifecycleRecord.occurrenceCount,

            occurredAt:
              lifecycleRecord.occurredAt,

            safeFailureMessage:
              lifecycleRecord.safeFailureMessage,
          }),
      );

    return Object.freeze({
      generatedAt:
        lifecycleSnapshot.generatedAt,

      totalRecords:
        records.length,

      records:
        Object.freeze(
          records,
        ),
    });
  };

export const clearCanonicalTreeHistoricalAuthorityPresentationObservabilityForTest =
  (): void => {
    clearCanonicalTreeHistoricalAuthorityPresentationLifecycleForTest();
  };

export const resolveCanonicalTreeHistoricalAuthorityPresentationObservabilityImplementation =
  (): CanonicalTreeHistoricalAuthorityPresentationObservabilityImplementation =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-079',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_IMPLEMENTATION',

      decision:
        'TREE_INTERNAL_DURABLE_CONTAINMENT_OBSERVABILITY_IMPLEMENTED',

      auditMilestone:
        'CC-TREE-ARCH-078',

      boundedHistoryCapacity:
        64,

      projectionFallbackPublicationImplemented:
        true,

      renderFailurePublicationImplemented:
        true,

      occurrenceTimestampImplemented:
        true,

      deterministicCorrelationIdImplemented:
        true,

      occurrenceCountImplemented:
        true,

      safeFailureMessageImplemented:
        true,

      existingTreeGovernorEvidenceReused:
        true,

      existingRuntimeEvidenceReused:
        true,

      canonicalGovernorRuntimeDispatchInvoked:
        false,

      projectionPayloadPublished:
        false,

      familyMemberDataPublished:
        false,

      stackTracePublished:
        false,

      runtimeDecisionDependencyAdded:
        false,

      runtimeGuardDependencyAdded:
        false,

      operationalCertificationDependencyAdded:
        false,

      memberFacingStateAdded:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      implementationCompleted:
        true,

      nextGovernedDependency:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_CERTIFICATION',
    });
